import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const SSO_ENABLED = !!process.env.REACT_APP_SSO_ENABLED;
const BACKEND_URL = process.env.NODE_ENV === 'production'
  ? 'https://masjid-app-funeral-production.up.railway.app'
  : (process.env.REACT_APP_API_URL?.replace('/api', '') || '');

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const urlError = new URLSearchParams(window.location.search).get('error');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-white/10 p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Al-Iman Funeral Support</h1>
          <p className="text-slate-400">Sign in to continue</p>
        </div>

        {(error || urlError) && (
          <div
            role="alert"
            aria-live="assertive"
            className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200"
          >
            {error || (urlError === 'not_invited'
              ? 'Your account has not been set up. Please contact an administrator.'
              : urlError === 'sso_failed'
              ? 'Microsoft sign-in failed. Please try again.'
              : 'Sign-in error. Please try again.')}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6" aria-label="Sign in form">
          <div>
            <label htmlFor="login-username" className="block text-slate-300 text-sm font-medium mb-2">
              Username
            </label>
            <input
              id="login-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
              autoFocus
              autoComplete="username"
              aria-required="true"
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>

          <div>
            <label htmlFor="login-password" className="block text-slate-300 text-sm font-medium mb-2">
              Password
            </label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
              autoComplete="current-password"
              aria-required="true"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            aria-disabled={loading}
            aria-busy={loading}
            className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {SSO_ENABLED && (
          <>
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-slate-500 text-xs">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <a
              href={`${BACKEND_URL}/api/auth/microsoft`}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white hover:bg-slate-100 text-slate-800 rounded-lg font-medium transition-all"
            >
              {/* Microsoft logo */}
              <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Sign in with Microsoft
            </a>
          </>
        )}

        <div className="mt-6 text-center">
          <p className="text-slate-500 text-xs">
            <a
              href="https://www.al-iman.co.uk/bereavement-intro"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-400 hover:text-indigo-300 transition-colors"
              aria-label="View Privacy Policy and Terms and Conditions (opens in new tab)"
            >
              Privacy Policy & Terms and Conditions
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
